import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
    BankAccount,
    DashboardClaim,
    FundingMethod,
    FileType
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    DownloadStaticFileRequest,
    DownloadStaticFileService,
    GetBankAccountsByClientRequest,
    GetBankAccountsByClientService,
    GetScheduleCoversheetDefaultsRequest,
    GetScheduleCoversheetDefaultsService,
    StaticFile
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    ClassNames,
    Color,
    DownloadService,
    ISelectOption,
    Modal,
    Styling,
    TourHelperService,
    Utilities
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    Icons,
    ResponsiveService
} from '@lsq-frontend/common-ui';
import { ModalService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { GuidedTour } from 'ngx-guided-tour';
import { combineLatest } from 'rxjs';
import { first } from 'rxjs/operators';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { TourAnalyticsNames, TourIds, TourSteps } from '../../../../constants/tours.constants';
import { ImagePreviewModalComponent } from '../../../image-preview/image-preview-modal.component';
import { InvoiceBatchService } from '../invoice-batch.service';
import { UploadInvoicesService } from '../upload-invoices.service';

@Component({
    selector: 'lsq-upload-invoices-cover-sheet',
    templateUrl: './upload-invoices-cover-sheet.component.html',
    styleUrls: ['./upload-invoices-cover-sheet.component.scss']
})
export class UploadInvoicesCoverSheetComponent implements OnInit, OnDestroy {
    @ViewChild('changeDisbursementMethodModalBody')
    public changeDisbursementMethodModalBody: TemplateRef<any>;

    // Constants.
    public invoiceFundingMethod = FundingMethod;
    public classNames = ClassNames;
    public icons = Icons;
    public color = Color;
    public claims = DashboardClaim;
    public defaultFundingMethodCopyI18n = this._i18n({
        value: 'Default',
        meaning: `Indicates that this funding method is the default`
    });

    // State.
    public coverSheetFormGroup: FormGroup;
    public availableBankAccountsForFunding: BankAccount[];
    public availableBankAccountsForFundingSelectOptions: ISelectOption<number>[];
    public selectedBankAccountForFunding: BankAccount;
    public selectedBankAccountForFundingSelectOption: ISelectOption<number>;
    public showPrivilegedFundingMethodOptions = false;
    public acceptContentTypes: string;
    public uploadInvoiceSummaryInput: HTMLInputElement;
    public defaultFundingMethod: FundingMethod;
    public isFreshForm = true;
    public coverSheetFormAmountsTotalLabel: string;
    public coverSheetFormAmountsCountLabel: string;
    public isMdBelow = false;
    public isLgBelow = false;

    private _componentIsAlive = true;

    constructor(
        private _getBankAccountsByClientService: GetBankAccountsByClientService,
        private _modalService: ModalService,
        private _downloadStaticFileService: DownloadStaticFileService,
        private _downloadService: DownloadService,
        private _claimService: ClaimService,
        private _invoiceBatchService: InvoiceBatchService,
        private _getScheduleCoverSheetDefaultsService: GetScheduleCoversheetDefaultsService,
        private _responsiveService: ResponsiveService,
        private _featureFlagsService: FeatureFlagService,
        private _tourHelperService: TourHelperService,
        private _i18n: I18n,
        public uploadInvoicesService: UploadInvoicesService
    ) {}

    public ngOnInit(): void {
        this.initializeForm(); // tslint:disable-line:no-floating-promises

        this._invoiceBatchService.coverSheetFormValueSubject.subscribe((formValue) => {
            if (!!formValue && !!formValue.totalAmount && !this.uploadInvoiceSummaryInput) {
                const {
                    inputElement
                } = this.uploadInvoicesService.initInvoiceSummaryFileUploadStream();
                this.uploadInvoiceSummaryInput = inputElement;
            }
        });

        this._claimService.claimStream.subscribe((claims) => {
            this.showPrivilegedFundingMethodOptions = claims[DashboardClaim.ViewRequestFunds];
        });

        this.acceptContentTypes = this.uploadInvoicesService.allowedContentTypesForInvoices.join(
            ','
        );

        this._responsiveService.lgBelow.subscribe((isLgBelow) => {
            this.isLgBelow = isLgBelow;
        });

        this._responsiveService.mdBelow.subscribe((isMdBelow) => {
            this.isMdBelow = isMdBelow;
        });

        this._responsiveService.smBelow.subscribe((isSmBelow) => {
            if (isSmBelow) {
                this.coverSheetFormAmountsTotalLabel = this._i18n({
                    value: 'Total Amount',
                    meaning:
                        `Label for the 'total amount' field on the 'cover sheet' ` +
                        `screen of the 'upload invoices' flow.`,
                    description: `Shortened for display on mobile devices`
                });
                this.coverSheetFormAmountsCountLabel = this._i18n({
                    value: 'Number of Invoices (Optional)',
                    meaning:
                        `Label for the optional 'number of invoices' field on the ` +
                        `'cover sheet' screen of the 'upload invoices' flow.`,
                    description: `Shortened for display on mobile devices`
                });
            } else {
                this.coverSheetFormAmountsTotalLabel = this._i18n({
                    value: 'Total Amount of Invoices Being Submitted',
                    meaning:
                        `Label for the 'total amount' field on the 'cover sheet' ` +
                        `screen of the 'upload invoices' flow.`
                });
                this.coverSheetFormAmountsCountLabel = this._i18n({
                    value: 'Number of Invoices Being Submitted (Optional)',
                    meaning:
                        `Label for the optional 'number of invoices' field on the ` +
                        `'cover sheet' screen of the 'upload invoices' flow.`
                });
            }
        });
    }

    public ngOnDestroy(): void {
        this._componentIsAlive = false;
    }

    public async initializeForm(): Promise<void> {
        this.coverSheetFormGroup = this._invoiceBatchService.coverSheetFormGroup;

        // Need to identify if we are visiting a fresh form, or if they have gone
        // back in the flow after filling it out.
        this.isFreshForm =
            !this.coverSheetFormGroup.value.totalAmount &&
            this.coverSheetFormGroup.value.totalAmount !== 0;

        const getBankAccountsByClientRequest = new GetBankAccountsByClientRequest();

        const response = await this._getBankAccountsByClientService.executeOnce(
            getBankAccountsByClientRequest
        );
        if (!!response && !!response.rows) {
            this.availableBankAccountsForFunding = response.rows;
            this.availableBankAccountsForFundingSelectOptions = this.availableBankAccountsForFunding.map(
                (bankAccount) => ({
                    label: bankAccount.accountName,
                    value: bankAccount.bankAccountKey
                })
            );
        }

        // We need to wait for the bank accounts to return before we start the tour.
        combineLatest(
            this._featureFlagsService.isOn(FeatureFlags.UploadInvoicesTour),
            this._responsiveService.smBelow,
            this._claimService.isOn(DashboardClaim.ViewRequestFunds)
        )
            .pipe(first())
            .subscribe(([featureFlag, isMobile, canRequestFunds]) => {
                if (featureFlag && !isMobile && canRequestFunds) {
                    const tourSteps = TourSteps.createUploadInvoiceTourSteps();
                    // Let the page render before starting tour.
                    const uploadTour: GuidedTour = this._tourHelperService.attachEndEvents(
                        {
                            tourId: TourIds.UploadIntro,
                            minimumScreenSize: Styling.mdStart,
                            steps: tourSteps
                        },
                        TourAnalyticsNames.UploadIntro
                    );

                    Utilities.executeAfterRender(() => {
                        if (this._componentIsAlive) {
                            // tslint:disable-next-line
                            this._tourHelperService.startTour(uploadTour);
                        }
                    });
                }
            });

        const getScheduleCoverSheetDefaultsResponse = await this._getScheduleCoverSheetDefaultsService.executeOnce(
            new GetScheduleCoversheetDefaultsRequest()
        );
        this.defaultFundingMethod = getScheduleCoverSheetDefaultsResponse.fundingMethod;

        // If we are seeing this page for the first time, overwrite with the defaults.
        if (this.isFreshForm) {
            this.coverSheetFormGroup.patchValue({
                fundingMethod: this.defaultFundingMethod,
                bankAccountKeyForDisbursement:
                    getScheduleCoverSheetDefaultsResponse.clientBankAccountKey
            });
        }
    }

    public downloadInvoiceSummaryTemplate(): void {
        const downloadStaticFileRequest = new DownloadStaticFileRequest({
            staticFile: StaticFile.InvoiceSummaryTemplate
        });
        this._downloadService.execute(
            this._downloadStaticFileService,
            downloadStaticFileRequest,
            FileType.Csv,
            'InvoiceSummaryTemplate.csv'
        );
    }

    public handleUploadInvoiceSummaryClick(): void {
        (this.uploadInvoiceSummaryInput as HTMLInputElement).click();
    }

    public showInvoiceSummaryPreviewModal(): void {
        const fileUpload = this.uploadInvoicesService.invoiceSummaryFileUpload;
        if (!!fileUpload) {
            this._modalService.showModal(
                new Modal({
                    id: 'image-preview-modal',
                    title: fileUpload.name,
                    component: ImagePreviewModalComponent,
                    width: 'auto',
                    data: {
                        title: fileUpload.name,
                        imageId: fileUpload.imageKey,
                        totalPages: fileUpload.pages,
                        filename: fileUpload.name
                    }
                })
            );
        }
    }

    public removeInvoiceSummaryUpload(): void {
        this.uploadInvoicesService.invoiceSummaryFileUpload.markForRemoval();
    }

    public shouldShowBankAccountField(): boolean {
        return (
            !!this.availableBankAccountsForFundingSelectOptions &&
            (this.coverSheetFormGroup.value.fundingMethod ===
                FundingMethod.InvoiceBatchDisbursement ||
                this.coverSheetFormGroup.value.fundingMethod ===
                    FundingMethod.InvoiceBatchAndAvailabilityDisbursement ||
                this.coverSheetFormGroup.value.fundingMethod === FundingMethod.SpecificAmount)
        );
    }
}
